import { useEffect, useState, useCallback } from 'react'
import DropDown from '../DropDown/DropDown'
import { CREATE_NEW, TSG_FIELD_NAME } from '../GlobalConstants'
import { PlusCircleIcon } from '@panwds/icons'
import type { IDropDownData, IDropDownProps } from '../DropDown/DropDown'
import type { ReadonlyTsgTree } from '@sparky/framework'
import { useFormContext } from 'react-hook-form'
import { StringTemplate, primaryAppAvailableAddonLicenses } from '../utils'
import { ACTIVATION_ERROR_CODE_TO_DISPLAY_MSG_MAP, ACTIVATION_UI_ERROR_CODES } from '../../../ErrorCodes'
import type { LockedInfo, CommonStoreState } from '../../../store'
import type { Addon } from '../../../renderer'
import FlattenTree from '../../../../common/utils/FlattenTree'
import { SUPER_ROOT_ID } from '../../../constants'
import { ELA, ERROR_API_IP_RESTRICTED_TSG } from '../../../../common/constants/GlobalConstants'
import { ifIpRestricted } from '../../../../common/context/IpRestrictContext'

const fieldName = TSG_FIELD_NAME

export type ITSGDropDown = Partial<Omit<IDropDownProps, 'fieldName'>> & {
  allTsgList: ReadonlyTsgTree
  currentTsg: any
  create_new?: boolean
  isLoading: boolean
  showErrorIfInstanceExists?: boolean
  customer_support_account?: string
  showAllTSG?: boolean
  primaryAppId?: string
  instances?: any
  primaryApp?: any
  lockedInfo?: LockedInfo
  callbacks: IDropDownProps['callbacks']
  showErrorIfPrimaryAppAddonExists?: LockedInfo;
  state: CommonStoreState,
  disable?: boolean,
}

const defaultProps = {
  display: {
    errorMessage: 'Select TSG to proceed activation',
    toolTipMessage: 'Select the TSG where the license needs to be provisioned',
    subTitle: 'The tenant where the license will be activated'
  },
  dropDownProps: {
    required: true,
    addNewValue: true,
    triggerValidationOnChange: true,
    addNewProps: {
      conditionValue: 'ADD_NEW',
      updatedValue: '',
      createValue: '[Create New]'
    }
  },
  size: {
    height: 32,
    width: 300
  },
  passByProps: {
    enableSearch: true,
    label: 'Tenant',
    dataMetrics: 'default-tsg-component',
    dataTestId: 'tsg-activation-screen',
    placeholder: 'Select Tenant / Create New',
    menuStyle: { width: 300 }
  }
}

const TSGDropDown = (props: ITSGDropDown) => {
  const {
    allTsgList = { children: [] },
    create_new = false,
    showErrorIfInstanceExists = false,
    customer_support_account = '',
    currentTsg, showAllTSG = false,
    state,
    instances = state.instances,
    primaryAppId = '',
    primaryApp = null,
    lockedInfo,
    showErrorIfPrimaryAppAddonExists = false,
    disable,
    ...passByProps
  } = props

  const { manifest, tsg, licenses } = state
  const [dropDownData, setDropDownData] = useState<IDropDownData[]>([])
  const [, setDropDownProps] = useState({ ...passByProps.dropDownProps })
  const { clearErrors, setError } = useFormContext()

  const addonErrors = useCallback((addons: Addon[]) => {
    addons.forEach((each: Addon) => {
      const existingPrimaryInstance = instances?.get(primaryAppId)
      const hasNewPrimaryLicense = licenses?.some(e => e.app_id === primaryAppId)
      const hasAddonNewLicense = licenses?.some(e => e.app_id === each.app_id && e?.licenses?.some(innerLicense => innerLicense.app_id === each.app_id && !innerLicense.is_csp_activated)) // exclude already activated 3p
      // if ML or existing TSG has primary app license/tenant then only allow addon activation
      if (manifest && tsg && !existingPrimaryInstance && !hasNewPrimaryLicense && primaryAppAvailableAddonLicenses(manifest, licenses)?.length) {
        clearErrors(fieldName)
        setError(fieldName, { message: StringTemplate(ACTIVATION_ERROR_CODE_TO_DISPLAY_MSG_MAP[ACTIVATION_UI_ERROR_CODES.ADD_ON_PRIMARY_INSTANCE_NOT_EXISTS], [manifest.description]) })
      }
      else if (hasAddonNewLicense) { //if ML has addon license then only validate this
        const existingAddonEntitlements = existingPrimaryInstance?.entitlements?.find((e: { app_id: string }) => e.app_id === each.app_id)
        if (existingAddonEntitlements || existingPrimaryInstance?.extra?.has_addon) {  // TODO remove has_addon once IT sends app_id for iot_3p)
          const displayName = existingAddonEntitlements?.display_name || each.display_name
          clearErrors(fieldName)
          setError(fieldName, { message: StringTemplate(ACTIVATION_ERROR_CODE_TO_DISPLAY_MSG_MAP[ACTIVATION_UI_ERROR_CODES.INSTANCE_ALREADY_EXISTS], [displayName]) })
        }
      }
    })
  }, [clearErrors, instances, licenses, manifest, primaryAppId, setError, tsg])

  useEffect(() => {
    let _ddData: any = allTsgList?.children || []
    // If showAllTSG is true, than flatten out all tsgs and remove 'All Tenant' item
    if (showAllTSG) {
      const dropdownDataSource = { children: [] }
      FlattenTree(_ddData, dropdownDataSource.children)
      _ddData = dropdownDataSource.children.filter((t) => t['id'] !== SUPER_ROOT_ID)
    }
    const _dropDownData = _ddData?.filter((each: any) => {
      if (showAllTSG) return each.id
      else return each.isAbsoluteRoot
    }).map((each: any) => {
      return {
        value: each.display_name,
        description: each.display_name,
        id: each.id,
        key: each.id,
        children: each.display_name,
      }
    }) ?? []
    if (create_new) {
      const createOption = {
        value: CREATE_NEW,
        description: CREATE_NEW,
        iconLeft: <PlusCircleIcon size='sm' className='cm-text-blue-600' />,
        id: CREATE_NEW,
        key: CREATE_NEW,
        children: CREATE_NEW,
      }
      _dropDownData.unshift(createOption)
    }
    setDropDownProps((prev: any) => {
      const _newProps = { ...prev.addNewProps }
      _newProps.updatedValue = `${customer_support_account} - ${_newProps.createValue}`
      return { ...prev, addNewProps: _newProps }
    })
    setDropDownData(_dropDownData)
  }, [allTsgList, create_new, customer_support_account, showAllTSG])

  useEffect(() => {
    if (showErrorIfInstanceExists) {
      clearErrors(fieldName)
      const appName = primaryApp?.display_name
      if (instances?.get(primaryAppId)) {
        setError(fieldName, { message: StringTemplate(ACTIVATION_ERROR_CODE_TO_DISPLAY_MSG_MAP[ACTIVATION_UI_ERROR_CODES.INSTANCE_ALREADY_EXISTS], [appName]) })
      }
    }
    if (showErrorIfPrimaryAppAddonExists) {
      const addons = manifest?.flags?.addons
      if (addons?.length) {
        addonErrors(addons)
      }
    }
    if (lockedInfo?.csp_id && instances?.length) {
      if (lockedInfo?.csp_id !== instances[0].support_account_id) {
        clearErrors(fieldName)
        if (lockedInfo?.mode?.toUpperCase() === ELA) {
          setError(fieldName, { message: 'The selected tenant does not match with selected ELA account.' })
        }
        else {
          setError(fieldName, { message: 'The selected tenant does not match with the support account from which the license was purchased.' })
        }
      }
    }
    if (instances?.error) {
      let message: string
      if (ifIpRestricted(instances.error)) {
        message = `${ERROR_API_IP_RESTRICTED_TSG} Please try another Tenant.`
      }
      else {
        message = `${instances.error.message ?? String(instances.error)} Please try another Tenant.`
      }
      setError(fieldName, { message })
    }
  }, [instances, showErrorIfInstanceExists, clearErrors, setError, primaryApp, primaryAppId, lockedInfo, showErrorIfPrimaryAppAddonExists, manifest, tsg, licenses, addonErrors])

  return <div>
    <DropDown
      {...{
        ...passByProps, dropDownList: dropDownData, defaultValue: currentTsg,
        disabled: disable,
        fieldName,
        display: {
          ...defaultProps.display,
          ...props.display
        },
        dropDownProps: {
          ...defaultProps.dropDownProps,
          ...props.dropDownProps,
        },
        passByProps: {
          ...defaultProps.passByProps,
          ...props.passByProps,
        },
        size: {
          ...defaultProps.size,
          ...props.size
        }
      }}
    />
  </div>
}
export default TSGDropDown
